import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { get, del } from "@/lib/api";
import client from "@/lib/api";
import { PageLoader, PageHeader, EmptyState, Badge, statusTone } from "@/components/common";
import { ScanText, UploadCloud, FileText, Sparkles, Loader2, CheckCircle2, ArrowRight, Trash2 } from "lucide-react";

const STEPS = ["Upload", "AI Scan", "Convert to MCQ", "Edit & Add", "Review & Publish", "Go Live"];

export default function OcrImport() {
  const qc = useQueryClient();
  const nav = useNavigate();
  const [file, setFile] = useState(null);
  const [scanning, setScanning] = useState(false);
  const { data, isLoading, refetch } = useQuery({ queryKey: ["ocr-jobs"], queryFn: () => get("/ocr/jobs"), refetchInterval: 4000 });

  const onDrop = (e) => { e.preventDefault(); if (e.dataTransfer.files[0]) setFile(e.dataTransfer.files[0]); };
  const deleteJob = async (id) => { if (!window.confirm("Delete this import and its extracted questions?")) return; await del(`/ocr/jobs/${id}`); toast.success("Import deleted"); qc.invalidateQueries({ queryKey: ["ocr-jobs"] }); };

  const startImport = async () => {
    if (!file) return toast.error("Please choose a file first");
    setScanning(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await client.post("/ocr/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((x) => x.data);
      // poll until completed then jump to review
      let tries = 0;
      const poll = setInterval(async () => {
        tries++;
        const jobs = await get("/ocr/jobs");
        const job = jobs.find((j) => j.id === r.job_id);
        if (job && (job.status === "completed" || job.status === "published")) {
          clearInterval(poll); setScanning(false); setFile(null);
          qc.invalidateQueries({ queryKey: ["ocr-jobs"] });
          toast.success("AI scan complete — questions ready to review");
          nav(`/teacher/ocr/${r.job_id}`);
        } else if (tries > 20) { clearInterval(poll); setScanning(false); refetch(); }
      }, 3000);
    } catch (e) { setScanning(false); toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      <PageHeader title="OCR / AI Content Import" subtitle="Turn handwritten notes, printed papers or study PDFs into a live mock test — in a few guided steps" />

      {/* Step tracker */}
      <div className="bg-white rounded-2xl border border-slate-200 p-4 mb-6 overflow-x-auto scroll-hide">
        <div className="flex items-center gap-2 min-w-max">
          {STEPS.map((s, i) => (
            <React.Fragment key={s}>
              <div className="flex items-center gap-2">
                <span className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold ${i === 0 ? "bg-indigo-800 text-white" : "bg-slate-100 text-slate-400"}`}>{i + 1}</span>
                <span className={`text-sm font-semibold ${i === 0 ? "text-slate-800" : "text-slate-400"}`}>{s}</span>
              </div>
              {i < STEPS.length - 1 && <ArrowRight className="h-4 w-4 text-slate-300" />}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Step 1: Upload */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6 mb-6">
        <div className="flex items-center gap-2 mb-4"><UploadCloud className="h-5 w-5 text-indigo-700" /><p className="font-display font-bold text-slate-900">Step 1 — Upload your document</p></div>
        <label onDragOver={(e) => e.preventDefault()} onDrop={onDrop}
          className="block border-2 border-dashed border-slate-300 rounded-xl p-10 text-center cursor-pointer hover:border-indigo-400 hover:bg-indigo-50/40 transition" data-testid="ocr-dropzone">
          <UploadCloud className="h-10 w-10 text-indigo-400 mx-auto mb-3" />
          <p className="text-base font-semibold text-slate-700">{file ? file.name : "Drag & drop, or click to browse"}</p>
          <p className="text-xs text-slate-500 mt-1">Supports PDF, JPG, PNG, DOCX — handwritten notes, printed question papers or study PDFs</p>
          <input type="file" accept=".pdf,.png,.jpg,.jpeg,.docx,.txt" className="hidden" onChange={(e) => setFile(e.target.files[0])} />
        </label>
        <button onClick={startImport} disabled={scanning || !file} data-testid="start-ocr-btn" className="mt-4 bg-violet-700 hover:bg-violet-800 disabled:opacity-50 text-white font-bold px-6 py-3 rounded-lg flex items-center gap-2">
          <ScanText className="h-4 w-4" /> Scan with AI
        </button>
        <p className="text-xs text-slate-400 mt-2">AI never auto-publishes and never invents answers — you review everything before it goes live.</p>
      </div>

      {/* Step 2: Scanning animation overlay */}
      {scanning && (
        <div className="fixed inset-0 z-50 bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="ocr-scanning-overlay">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center">
            <div className="relative h-20 w-20 mx-auto mb-5">
              <div className="absolute inset-0 rounded-2xl bg-violet-100" />
              <ScanText className="absolute inset-0 m-auto h-9 w-9 text-violet-700" />
              <div className="absolute left-0 right-0 h-0.5 bg-violet-500 shadow-[0_0_10px_2px_rgba(124,58,237,0.6)] animate-[scan_1.6s_ease-in-out_infinite]" />
            </div>
            <h3 className="font-display font-extrabold text-lg text-slate-900">AI Scanning Text, Numbers, Images (diagrams) and Layout…</h3>
            <p className="text-sm text-slate-500 mt-2">Reading your document and structuring questions. This takes a few seconds.</p>
            <Loader2 className="h-5 w-5 animate-spin text-violet-600 mx-auto mt-4" />
          </div>
          <style>{`@keyframes scan{0%{top:8px}50%{top:64px}100%{top:8px}}`}</style>
        </div>
      )}

      <h2 className="font-display font-bold text-lg mb-3">Your Imports</h2>
      {isLoading ? <PageLoader /> : !data?.length ? <EmptyState icon={ScanText} title="No imports yet" hint="Upload a file above to get started." /> : (
        <div className="space-y-3">{data.map((j) => (
          <div key={j.id} className="bg-white rounded-xl border border-slate-200 p-4 flex items-center justify-between">
            <div className="flex items-center gap-3"><div className="h-10 w-10 rounded-lg bg-violet-50 text-violet-700 flex items-center justify-center"><FileText className="h-5 w-5" /></div>
              <div><button onClick={() => (j.status === "completed" || j.status === "published") && nav(`/teacher/ocr/${j.id}`)} className="font-semibold text-slate-800 hover:text-indigo-800 text-left">{j.filename}</button><p className="text-xs text-slate-500">{j.question_count} questions · {new Date(j.created_at).toLocaleString()}</p></div></div>
            <div className="flex items-center gap-3">
              <Badge tone={statusTone(j.status)}>{j.status === "processing" || j.status === "queued" ? <span className="flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> {j.status}</span> : j.status}</Badge>
              {(j.status === "completed" || j.status === "published") && <button onClick={() => nav(`/teacher/ocr/${j.id}`)} data-testid={`review-job-${j.id}`} className="bg-indigo-800 text-white text-sm font-semibold px-4 py-1.5 rounded-lg flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> Review & Publish</button>}
              <button onClick={() => deleteJob(j.id)} data-testid={`delete-job-${j.id}`} className="text-red-500"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>))}</div>
      )}
    </div>
  );
}
